"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useIsVerticallyScrolled = void 0;

var _react = _interopRequireDefault(require("react"));

var _throttle = _interopRequireDefault(require("lodash/throttle"));

var _isServerSide = require("./isServerSide");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

/**
 * This hook provides the status of window or HTML element's scroll top position
 * if it meets the threshold provided.
 *
 * @param {Object} settings Hook Settings
 * @param {Integer} settings.scrollTopThreshold Threshold value to check on.
 * @param {HTMLElement} settings.scrollElementRef React mutable ref Object
 * returned by `useRef`. Defaults to Window.
 * @param {Function} settings.onScrollThresholdMet Callback invoked every time
 * after scroll top threshold is met.
 * @param {Function} settings.onScrollThresholdMetOnce This callback is
 * called only once even though threshold is met multiple times.
 *
 * @returns {Boolean} Element scroll position status.
 * `true` if greater than threshold and `false` if smaller.
 */
var useIsVerticallyScrolled = function useIsVerticallyScrolled(_ref) {
  var scrollTopThreshold = _ref.scrollTopThreshold,
      scrollElementRef = _ref.scrollElementRef,
      _ref$onScrollThreshol = _ref.onScrollThresholdMet,
      onScrollThresholdMet = _ref$onScrollThreshol === void 0 ? function () {} : _ref$onScrollThreshol,
      _ref$onScrollThreshol2 = _ref.onScrollThresholdMetOnce,
      onScrollThresholdMetOnce = _ref$onScrollThreshol2 === void 0 ? function () {} : _ref$onScrollThreshol2;

  var _React$useState = _react["default"].useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isThresholdMet = _React$useState2[0],
      setIsThresholdMet = _React$useState2[1]; // To avoid unnecessary state update, we are using this ref
  // as an flag to check if scroll position is below or above
  // provide scroll threshold.


  var scrollRef = _react["default"].useRef(false); // This Ref flag is used to determine if `onThresholdMetOnce`
  // callback is already called.


  var thresholdMetOnceRef = _react["default"].useRef(false); // This is to avoid stale data issue with Closures. Ref is a
  // mutable object hence its used to hold the snapshot of the
  // latest memoized scroll element data.


  var scrollElementAndScrollTopPropertyRef = _react["default"].useRef([]); // Memoizing the scroll element and `scrollTop` property as we
  // don't want to keep checking the element type every time the
  // scroll handler is called. Once its determined in the beginning
  // its not going to updated.


  var memoizedScrollElementAndScrollTopProperty = _react["default"].useMemo(function () {
    // This means, scroll container element is `window`.
    if (!(0, _isServerSide.isServerSide)() && scrollElementRef === undefined) {
      return [window, 'scrollY'];
    } else if (scrollElementRef !== null && scrollElementRef !== void 0 && scrollElementRef.current) {
      return [scrollElementRef === null || scrollElementRef === void 0 ? void 0 : scrollElementRef.current, 'scrollTop'];
    }

    return [];
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [scrollElementRef === null || scrollElementRef === void 0 ? void 0 : scrollElementRef.current]); // Scroll event handler is attached only once, hence scroll
  // handler will take the snapshot of the dependency at the
  // time it was defined. Initially, if scrollElementRef is provided,
  // its current value is null or default value, hence scroll
  // handler will always use that default value every time its
  // invoked even though scrollElementRef value is updated to DOM
  // element. Hence, using ref.current to hold the latest/updated
  // scrollElementRef data.


  _react["default"].useEffect(function () {
    scrollElementAndScrollTopPropertyRef.current = memoizedScrollElementAndScrollTopProperty; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollElementRef === null || scrollElementRef === void 0 ? void 0 : scrollElementRef.current]);

  var scrollHandler = function scrollHandler() {
    if (!scrollElementAndScrollTopPropertyRef.current.length) return;
    var scrollEl = scrollElementAndScrollTopPropertyRef.current[0];
    var scrollTopProperty = scrollElementAndScrollTopPropertyRef.current[1]; // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    var scrollTop = scrollEl[scrollTopProperty] || 0;

    if (scrollTop > scrollTopThreshold && !scrollRef.current) {
      scrollRef.current = true;
      onScrollThresholdMet();

      if (!thresholdMetOnceRef.current) {
        onScrollThresholdMetOnce();
        thresholdMetOnceRef.current = true;
      }

      setIsThresholdMet(true);
    } else if (scrollTop < scrollTopThreshold && scrollRef.current) {
      scrollRef.current = false;
      setIsThresholdMet(false);
    }
  };

  _react["default"].useEffect(function () {
    var scrollElement = scrollElementAndScrollTopPropertyRef.current[0];
    var throttledScrollHandler = (0, _throttle["default"])(scrollHandler, 100);
    scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.addEventListener('scroll', throttledScrollHandler);
    return function () {
      scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.removeEventListener('scroll', throttledScrollHandler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return isThresholdMet;
};

exports.useIsVerticallyScrolled = useIsVerticallyScrolled;